import { getRuntimeConfig } from './runtime-config'

interface EnvironmentConfig {
  readonly BACKEND_BASE_URL: string
  readonly USER_INITIALIZE_PATH: string
  readonly USER_ONBOARD_PATH: string
  readonly API_KEY_GENERATE_PATH: string
  readonly API_KEY_LIST_PATH: string
  readonly API_KEY_REVOKE_PATH: string
  readonly HOST_ADD_PATH: string
  readonly HOST_LIST_PATH: string
  readonly HOST_LIST_PAGINATED_PATH: string
  readonly HOST_DELETE_PATH: string
  readonly HOST_PROJECTS_PATH: string
  readonly SESSIONS_LATEST_PATH: string
  readonly COMMAND_SUBMIT_PATH: string
  readonly SSE_CONVERSATION_CONNECT_PATH: string
  readonly SSE_CONVERSATION_DISCONNECT_PATH: string
  readonly SSE_ACTIVITY_CONNECT_PATH: string
  readonly SSE_ACTIVITY_DISCONNECT_PATH: string
  readonly SSE_CONVERSATION_STREAM_NAME: string
  readonly SSE_CONVERSATION_EVENT_NAME: string
  readonly SSE_CONVERSATION_CONNECTION_MESSAGE: string
  readonly SSE_ACTIVITY_STREAM_NAME: string
  readonly SSE_ACTIVITY_EVENT_NAME: string
  readonly SSE_ACTIVITY_CONNECTION_MESSAGE: string
  readonly SSE_STATUS_CONNECT_PATH: string
  readonly SSE_STATUS_DISCONNECT_PATH: string
  readonly SSE_STATUS_STREAM_NAME: string
  readonly SSE_STATUS_SESSION_EVENT_NAME: string
  readonly SSE_STATUS_HOST_EVENT_NAME: string
  readonly SSE_STATUS_CONNECTION_MESSAGE: string
  readonly SESSION_STATUSES_PATH: string
  readonly HOST_STATUSES_PATH: string
  readonly SSE_RECONNECT_INITIAL_DELAY_MS: number
  readonly SSE_RECONNECT_MAX_DELAY_MS: number
  readonly SSE_RECONNECT_BACKOFF_MULTIPLIER: number
  readonly SSE_RECONNECT_MAX_ATTEMPTS: number
  readonly HTTP_CLIENT_TIMEOUT_MS: number
  readonly KEYCLOAK_BASE_URL: string
  readonly KEYCLOAK_REALM: string
  readonly KEYCLOAK_CLIENT_ID: string
  readonly OIDC_SILENT_REQUEST_TIMEOUT_MS: number
  readonly OIDC_SESSION_CHECK_INTERVAL_MS: number
  readonly OIDC_TOKEN_EXPIRING_NOTIFICATION_SECONDS: number
  readonly USER_INIT_MAX_ATTEMPTS: number
  readonly USER_INIT_RETRY_DELAY_MS: number
  readonly NOTIFICATION_AUTO_CLOSE_MS: number
  readonly NOTIFICATION_WELCOME_AUTO_CLOSE_MS: number
  readonly NOTIFICATION_PERMISSION_CHECK_INTERVAL_MS: number
  readonly QUERY_RETRY_ATTEMPTS: number
  readonly QUERY_RETRY_MAX_DELAY_MS: number
  readonly QUERY_STALE_TIME_MS: number
  readonly APP_NAME: string
  readonly APP_VERSION: string
  readonly ENABLE_DEVTOOLS: boolean
  readonly ENABLE_MSW: boolean
}

/**
 * Build-time constants that do not change between environments.
 * These are application defaults and API paths that are version-controlled.
 */
const ENV_DEFAULTS = {
  // API Paths
  USER_INITIALIZE_PATH: '/api/v1/users/initialize',
  USER_ONBOARD_PATH: '/api/v1/users/onboard',
  API_KEY_GENERATE_PATH: '/api/v1/api-keys/generate',
  API_KEY_LIST_PATH: '/api/v1/api-keys/list',
  API_KEY_REVOKE_PATH: '/api/v1/api-keys/revoke',
  HOST_ADD_PATH: '/api/v1/hosts/add',
  HOST_LIST_PATH: '/api/v1/hosts/list',
  HOST_LIST_PAGINATED_PATH: '/api/v1/hosts/list',
  HOST_DELETE_PATH: '/api/v1/hosts/delete',
  HOST_PROJECTS_PATH: '/api/v1/hosts/projects',
  SESSIONS_LATEST_PATH: '/api/v1/sessions/latest',
  COMMAND_SUBMIT_PATH: '/api/v1/users/commands/submit',

  // SSE Paths
  SSE_CONVERSATION_CONNECT_PATH: '/api/v1/claude-code/events/stream/conversation/connect',
  SSE_CONVERSATION_DISCONNECT_PATH: '/api/v1/claude-code/events/stream/conversation/disconnect',
  SSE_ACTIVITY_CONNECT_PATH: '/api/v1/claude-code/events/stream/activity/connect',
  SSE_ACTIVITY_DISCONNECT_PATH: '/api/v1/claude-code/events/stream/activity/disconnect',

  // SSE Configuration
  SSE_CONVERSATION_STREAM_NAME: 'claude-code-conversation-events-stream',
  SSE_CONVERSATION_EVENT_NAME: 'claude-code-conversation-event',
  SSE_CONVERSATION_CONNECTION_MESSAGE: 'Connected to Claude Code conversation events stream',
  SSE_ACTIVITY_STREAM_NAME: 'claude-code-activity-events-stream',
  SSE_ACTIVITY_EVENT_NAME: 'claude-code-activity-event',
  SSE_ACTIVITY_CONNECTION_MESSAGE: 'Connected to Claude Code activity events stream',

  // SSE Status Stream
  SSE_STATUS_CONNECT_PATH: '/api/v1/claude-code/events/stream/status/connect',
  SSE_STATUS_DISCONNECT_PATH: '/api/v1/claude-code/events/stream/status/disconnect',
  SSE_STATUS_STREAM_NAME: 'claude-code-status-events-stream',
  SSE_STATUS_SESSION_EVENT_NAME: 'session-status-change',
  SSE_STATUS_HOST_EVENT_NAME: 'host-status-change',
  SSE_STATUS_CONNECTION_MESSAGE: 'Connected to Claude Code status events stream',

  // Hydration REST Endpoints
  SESSION_STATUSES_PATH: '/api/v1/sessions/status',
  HOST_STATUSES_PATH: '/api/v1/hosts/status',

  SSE_RECONNECT_INITIAL_DELAY_MS: 1000,
  SSE_RECONNECT_MAX_DELAY_MS: 30000,
  SSE_RECONNECT_BACKOFF_MULTIPLIER: 2,
  SSE_RECONNECT_MAX_ATTEMPTS: 10,

  // HTTP Client
  HTTP_CLIENT_TIMEOUT_MS: 15000,

  // OIDC Configuration
  OIDC_SILENT_REQUEST_TIMEOUT_MS: 15000,
  OIDC_SESSION_CHECK_INTERVAL_MS: 10000,
  OIDC_TOKEN_EXPIRING_NOTIFICATION_SECONDS: 300,

  // User Initialization
  USER_INIT_MAX_ATTEMPTS: 5,
  USER_INIT_RETRY_DELAY_MS: 2000,

  // Notifications
  NOTIFICATION_AUTO_CLOSE_MS: 8000,
  NOTIFICATION_WELCOME_AUTO_CLOSE_MS: 5000,
  NOTIFICATION_PERMISSION_CHECK_INTERVAL_MS: 5000,

  // React Query
  QUERY_RETRY_ATTEMPTS: 3,
  QUERY_RETRY_MAX_DELAY_MS: 30000,
  QUERY_STALE_TIME_MS: 300000,

  // Application
  APP_NAME: 'Claude Code Hooks Dashboard',
  APP_VERSION: '0.1.0',
} as const

const runtimeConfig = getRuntimeConfig()

export const env: EnvironmentConfig = {
  BACKEND_BASE_URL: runtimeConfig.BACKEND_BASE_URL,
  KEYCLOAK_BASE_URL: runtimeConfig.KEYCLOAK_BASE_URL,
  KEYCLOAK_REALM: runtimeConfig.KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID: runtimeConfig.KEYCLOAK_CLIENT_ID,
  ENABLE_DEVTOOLS: runtimeConfig.ENABLE_DEVTOOLS,
  ENABLE_MSW: runtimeConfig.ENABLE_MSW,
  ...ENV_DEFAULTS,
} as const
